import { api } from '../../lib/api';

/**
 * `earned` is the one nobody can buy - see `DiscoveryCredits`. It is called
 * that rather than by its product name for the reason §1.3 gives: the brand's
 * vocabulary never reaches the API. `copy.ts` holds what people read.
 *
 * It sits above the paid tiers on purpose, and below a host pick, which is
 * pinned rather than weighed.
 */
export type BoostTier = 'free' | 'basic' | 'priority' | 'earned' | 'pick';
export type Submissions = 'open' | 'followers' | 'closed';

export interface RoomTrack {
  id: number;
  title: string;
  slug: string;
  duration_ms: number | null;
  explicit: boolean;
  artwork: string | null;
  artwork_small: string | null;
  waveform_url: string | null;
  artist: { username: string; display_name: string };
}

export interface Room {
  id: number;
  /** What tonight's set is called. Null is fine and common. */
  name: string | null;
  /** The address: a broadcast lives at its host's profile. */
  username: string;
  description: string | null;
  genre: string | null;
  status: 'offline' | 'live';
  source: 'catalogue' | 'stream';
  submissions: Submissions;
  /**
   * Whether this room takes tracks the artist marked explicit.
   *
   * Public rather than host-only: somebody about to submit needs to know
   * before they choose a track, not after the room refuses it.
   */
  accepts_explicit: boolean;
  auto_weight: boolean;
  /**
   * Whether a finished track rolls into the next one.
   *
   * Off means the room holds at the end so the host can review what just
   * played before moving on - the review box is keyed to whatever is on the
   * deck, so a room that advances on its own gives them a window measured in
   * how fast they noticed.
   */
  auto_play: boolean;
  /**
   * Whether listeners see what is coming up.
   *
   * The count is visible either way - `session.queued` - because "is there a
   * queue and how long is it" is what somebody deciding whether to submit
   * needs. This hides the contents: which tracks, in what order, at what tier.
   *
   * Enforced by `ShowStationAction`, which sends an empty `queue` to everybody
   * but the host when this is off. The flag is here so a listener's page can
   * tell "the host turned it off" apart from "nothing is queued", which look
   * identical from an empty array.
   */
  show_queue: boolean;
  /**
   * Where else this set is being streamed.
   *
   * Resolved by the server from the host's own profile link rather than stored
   * on the station, so it is null both when they have not named a platform and
   * when they named one whose link they have since removed - a badge must never
   * outlive the address it points at.
   */
  simulcast: { platform: string; label: string; url: string } | null;
  listener_count: number;
  peak_listeners: number;
  live_started_at: string | null;
  host: { username: string; display_name: string; avatar: string | null };

  /**
   * The two halves of the playhead, always together.
   *
   * `position_ms` alone is stale the moment it arrives; `server_time` is what
   * makes it correctable. See `sync.ts` - nothing should read one without the
   * other.
   */
  position_ms: number | null;
  server_time: string;
  paused: boolean;
  /**
   * Paused at the very end of a finished track, rather than mid-track.
   *
   * Derived by the server, never stored. It is the difference between "the host
   * stepped away" and "the track ended and the host is deciding", and showing
   * the first when it is the second reads as a stalled broadcast.
   */
  holding: boolean;
  now_playing: RoomTrack | null;

  /** Host only. A bearer credential for the public overlay URL. */
  overlay_token?: string;
}

export interface QueueItem {
  id: number;
  position: number;
  track: {
    id: number;
    title: string;
    slug: string;
    duration_ms: number | null;
    explicit: boolean;
    artwork_small: string | null;
    genre: string | null;
  };
  artist: { id: number; username: string; display_name: string };
  /** Null once the submitter has deleted their account. */
  submitter: { username: string; display_name: string } | null;
  boost_tier: BoostTier;
  weight: number | null;
  pinned: boolean;
  /*
   * The inputs, not just the total. A host looking at 3.5 next to 3.0 should be
   * able to see which bonus made the difference.
   */
  first_time_artist: boolean;
  submitter_in_room: boolean;
  starts_in_ms: number;
  created_at: string;
  /**
   * The submitter's private message to the host.
   *
   * Present only on the console's copy of the queue - `StationPresenter` omits
   * the key entirely for everybody else, so `undefined` here means "not yours
   * to see" rather than "they left it blank".
   */
  note?: string | null;
}

/**
 * The rules, generated by the server from `Weighting`.
 *
 * Rendered rather than hardcoded, so the legend under the queue cannot describe
 * an order the sorter does not produce.
 */
export interface Weighting {
  tiers: { tier: BoostTier; label: string; weight: number | null; pinned?: boolean }[];
  bonuses: { label: string; weight: number }[];
  tie_break: string;
}

/**
 * A host's verdict on a track they played.
 *
 * `private` is a crate note the artist never sees; `sent` notified them. One
 * shape for both, because a host writes them in the same box and deciding
 * which it is happens at the moment they press a button, not before.
 */
export interface Review {
  id: number;
  rating: number | null;
  body: string | null;
  visibility: 'sent' | 'private';
  updated_at: string;
}

export interface RoomView {
  /**
   * Null for somebody who has never hosted, which is most people.
   *
   * Not a 404: a profile page asks this for everybody it renders, and treating
   * the ordinary case as an error would mean every profile logged one.
   */
  room: Room | null;
  is_host: boolean;
  queue: QueueItem[];
  weighting: Weighting;
  session: {
    tracks_played: number;
    skipped_early: number;
    artists_played: number;
    queued: number;
    boosted: number;
    queue_ms: number;
  };
  /**
   * Listener samples over the last 90 minutes. Host-only, and empty until the
   * sweep has taken a few - which is a few minutes into a broadcast.
   */
  curve?: { at: string; listeners: number }[];
}

/**
 * Somebody's own earned-boost standing.
 *
 * `recent` is what makes this worth showing at all - see `EarnedBoosts`. The
 * track and host are nullable because either can be deleted after the credit
 * was earned, and the credit stays earned regardless.
 */
export interface BoostSummary {
  points: number;
  available: number;
  threshold: number;
  max_held: number;
  /** Progress through the current threshold, not the lifetime total. */
  toward_next: number;
  /**
   * The rules, from the server.
   *
   * Sent rather than hardcoded so a panel explaining how to earn cannot
   * describe a rule the server does not apply — same reason `weighting` comes
   * down with the queue.
   */
  rules: { per_play: number; artist_cooldown_days: number; daily_cap: number };
  recent: {
    points: number;
    reason: string;
    at: string;
    track: string | null;
    host: string | null;
    host_username: string | null;
  }[];
}

export interface LiveRoom {
  id: number;
  name: string | null;
  username: string;
  description: string | null;
  genre: string | null;
  listener_count: number;
  live_started_at: string | null;
  host: { username: string; display_name: string };
  now_playing: { title: string; artist: string; artwork_small: string | null } | null;
  /*
   * The two questions an artist scanning this list actually has: will they take
   * mine, and how long is the wait. Answered here rather than after a click,
   * because otherwise every "no" costs a page load.
   */
  submissions: Submissions;
  queue_count: number;
  simulcast: { platform: string; label: string; url: string } | null;
}

export interface ChatMessage {
  id: number;
  /** Null when a moderator removed it; the row keeps its place in the thread. */
  body: string | null;
  created_at: string;
  from: { username: string; display_name: string } | null;
}

/**
 * A broadcast belongs to a person, so it is addressed through them.
 *
 * `live(username)` for anybody watching, `mine()` for the host's own console.
 * There is no room id in any URL: one person has one station, so there is
 * nothing to choose between.
 */
export const rooms = {
  onAir: () => api.get<{ items: LiveRoom[] }>('/live'),
  live: (username: string) => api.get<RoomView>(`/users/${username}/live`),

  /** The console's view. Creates the station on first call - see MyStationAction. */
  mine: () => api.get<RoomView>('/me/station'),
  settings: (patch: Record<string, unknown>) => api.patch<RoomView>('/me/station', patch),

  goLive: () => api.post<{ room: Room }>('/me/station/live'),
  endBroadcast: () => api.delete<{ room: Room }>('/me/station/live'),

  transport: (action: 'play' | 'pause' | 'next' | 'seek', positionMs?: number) =>
    api.post<{ room: Room }>('/me/station/transport', { action, position_ms: positionMs }),

  setTier: (itemId: number, tier: BoostTier) =>
    api.patch(`/me/station/queue/${itemId}`, { tier }),

  /** `after: null` means "to the top". Turns auto-weight off server-side. */
  move: (itemId: number, after: number | null) =>
    api.patch(`/me/station/queue/${itemId}`, { after }),

  drop: (itemId: number) => api.delete(`/me/station/queue/${itemId}`),

  /**
   * The host adding to their own queue.
   *
   * Its own endpoint rather than `submit` with the host's own username, because
   * `pick` is host-only on the public one - so pinning would be a second
   * request with the room watching the gap. See ConsoleQueueAction.
   */
  add: (trackId: number, pin = false) =>
    api.post<{ id: number; pinned: boolean; first_time_artist: boolean }>('/me/station/queue', {
      track_id: trackId,
      pin,
    }),

  review: (trackId: number) =>
    api.get<{ review: Review | null }>(`/me/station/reviews/${trackId}`),

  saveReview: (trackId: number, body: { rating: number | null; body: string; visibility: Review['visibility'] }) =>
    api.put<{ review: Review }>(`/me/station/reviews/${trackId}`, body),

  /** What you have earned, how close the next one is, and why. */
  boosts: () => api.get<BoostSummary>('/me/boosts'),

  submit: (username: string, trackId: number, tier: BoostTier = 'free', note?: string | null) =>
    api.post<{ id: number; weight: number; first_time_artist: boolean; submitter_in_room: boolean }>(
      `/users/${username}/live/queue`,
      { track_id: trackId, tier, note },
    ),

  heartbeat: (username: string) =>
    api.post<{ listener_count: number }>(`/users/${username}/live/listeners`),
  leave: (username: string) => api.delete(`/users/${username}/live/listeners`),

  chat: (username: string) => api.get<{ items: ChatMessage[] }>(`/users/${username}/live/chat`),
  /** Host or staff only; the server checks, this just asks. */
  removeMessage: (username: string, id: number) =>
    api.delete<{ deleted: boolean }>(`/users/${username}/live/chat/${id}`),

  say: (username: string, body: string) =>
    api.post<{ id: number }>(`/users/${username}/live/chat`, { body }),
};

/** `4:07`, or an em dash while the duration is unknown. */
export function duration(ms: number | null): string {
  if (ms === null) return '—';

  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** `01:14:22`, for a broadcast that has been running a while. */
export function elapsed(since: string | null): string {
  if (since === null) return '00:00:00';

  const started = Date.parse(since.replace(' ', 'T') + 'Z');
  const total = Math.max(0, Math.floor((Date.now() - started) / 1000));

  return [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');
}

/** "in 4 min", for a queue position. Coarse, because it is an estimate. */
export function startsIn(ms: number): string {
  if (ms <= 0) return 'next';

  const minutes = Math.round(ms / 60000);

  return minutes < 1 ? 'in under a minute' : `in ${minutes} min`;
}

/**
 * The colour a tier is drawn in.
 *
 * The design's three signal colours, which already exist as design tokens:
 * indigo for a basic boost, red for priority, green for the host's own pick.
 */
export const TIER_TONE: Record<BoostTier, string> = {
  free: 'text-faint',
  basic: 'text-accent',
  priority: 'text-hot-text',
  // Its own colour, because it is the only tier nobody can buy and a queue
  // where it read the same as a paid one would be missing the point of it.
  earned: 'text-fresh',
  pick: 'text-fresh',
};
